import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Icon } from 'components/Icon';
import { lineHeight, theme, toRem } from 'design';
import { Text } from '../Typography';

interface IAltAccordionProps {
  children?: React.ReactNode;
  className?: string;
  defaultOpenState?: boolean;
  leftContent: string | React.ReactNode;
  rightContent?: string | React.ReactNode;
  dataCy?: string;
}

const ContentContainer = styled(motion.div)`
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: stretch;
  overflow: hidden;
`;

const DropdownIcon = styled(Icon)`
  &.open {
    transform: rotate(180deg);
  }
`;

const DropdownIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${toRem(32)};
  height: ${toRem(32)};
  margin-right: ${toRem(theme.space.sm)};
  background: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[300]};
  border-radius: 50%;
`;

const LeftTriggerContent = styled(Text)`
  display: block;
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  font-weight: ${theme.font.weight.medium};

  &.truncateText {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${theme.color.neutral[200]};
  min-width: ${toRem(24)};
  margin: 0 ${toRem(theme.space.lg)};
`;

const AltAccordionLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;
`;

const RightTriggerContent = styled(Text)`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  font-weight: ${theme.font.weight.medium};
`;

const Trigger = styled.button`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  white-space: nowrap;
`;

const AltAccordion: React.FC<IAltAccordionProps> = ({ children, className = '', defaultOpenState, leftContent, rightContent, dataCy }) => {
  const [open, setOpen] = useState(!!defaultOpenState);

  const onTriggerClick = () => setOpen(!open);

  return (
    <AltAccordionLayout className={className} data-cy={dataCy}>
      <Trigger onClick={onTriggerClick}>
        <DropdownIconWrapper>
          <DropdownIcon className={open ? 'open' : ''} icon="Dropdown" fill={theme.color.neutral[500]} />
        </DropdownIconWrapper>
        <LeftTriggerContent className={typeof leftContent === 'string' ? 'truncateText' : ''}>{leftContent}</LeftTriggerContent>
        {rightContent && (
          <>
            <StyledLine />
            <RightTriggerContent>{rightContent}</RightTriggerContent>
          </>
        )}
      </Trigger>
      <ContentContainer initial={open} animate={{ height: open ? 'auto' : 0, marginTop: open ? toRem(12) : 0 }} transition={theme.transition.spring.accordion}>
        {children}
      </ContentContainer>
    </AltAccordionLayout>
  );
};

export default AltAccordion;
